import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Hash, ArrowRight, ScanLine } from "lucide-react";
import Header from "../components/Header";
import { Input } from "../components/ui/input";
import QRScanner from "../components/QRScanner";
import { toast } from "sonner";
import checklistsData from "../mocks/checklists.json";

export default function NewInspection() {
  const [selected, setSelected] = useState(null);
  const [serial, setSerial] = useState("");
  const [qrOpen, setQrOpen] = useState(false);
  const navigate = useNavigate();

  const handleQrResult = (text) => {
    const cleaned = String(text || "").trim().toUpperCase();
    setSerial(cleaned);
    toast.success("QR leído correctamente", { description: `SN: ${cleaned}` });
  };

  const proceed = () => {
    if (!selected || !serial.trim()) return;
    navigate(`/checklist/${selected.id}?serial=${encodeURIComponent(serial.trim())}`);
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-white dark:bg-zinc-950" data-testid="new-inspection-page">
      <Header title="Nueva Inspección" subtitle="// Selecciona pieza" />
      <div className="flex-1 overflow-y-auto no-scrollbar px-5 pt-4 pb-24">
        <div className="mb-5">
          <label className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-600 dark:text-zinc-400 flex items-center gap-1.5 mb-2">
            <Hash className="w-3 h-3 text-orange-500" />
            Número de serie
          </label>
          <div className="flex gap-2">
            <Input
              data-testid="new-inspection-serial"
              value={serial}
              onChange={(e) => setSerial(e.target.value.toUpperCase())}
              placeholder="SN-0000-0000"
              className="flex-1 bg-zinc-50 dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 text-zinc-900 dark:text-white font-mono-tech h-12 rounded-sm focus-visible:ring-1 focus-visible:ring-orange-500 focus-visible:border-orange-500 placeholder:text-zinc-400 dark:placeholder:text-zinc-700 tracking-wider"
            />
            <button
              type="button"
              onClick={() => setQrOpen(true)}
              data-testid="new-inspection-qr-btn"
              aria-label="Escanear QR"
              title="Escanear QR"
              className="shrink-0 w-12 h-12 rounded-sm bg-orange-500 hover:bg-orange-600 text-white transition-colors flex items-center justify-center glow-orange active:scale-[0.98]"
            >
              <ScanLine className="w-5 h-5" />
            </button>
          </div>
        </div>

        <QRScanner
          open={qrOpen}
          onClose={() => setQrOpen(false)}
          onResult={handleQrResult}
        />

        <p className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-500 mb-3">
          // Tipo de pieza
        </p>

        <div className="grid grid-cols-2 gap-3" data-testid="parts-grid">
          {checklistsData.parts.map((part) => {
            const active = selected?.id === part.id;
            return (
              <button
                key={part.id}
                onClick={() => setSelected(part)}
                data-testid={`part-card-${part.id}`}
                className={`relative h-40 rounded-md overflow-hidden border transition-all text-left group ${
                  active
                    ? "border-orange-500 ring-1 ring-orange-500"
                    : "border-zinc-200 dark:border-zinc-800 hover:border-zinc-400 dark:hover:border-zinc-600"
                }`}
              >
                <img
                  src={part.image}
                  alt={part.name}
                  className="absolute inset-0 w-full h-full object-cover transition-transform group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
                {active && (
                  <div className="absolute top-2 right-2 z-20 px-2 py-0.5 bg-orange-500 rounded-sm">
                    <span className="font-mono-tech text-[9px] uppercase tracking-widest text-white font-bold">
                      Sel.
                    </span>
                  </div>
                )}
                <div className="absolute top-2 left-2 z-20">
                  <span className="font-mono-tech text-[9px] uppercase tracking-widest text-zinc-200 bg-black/60 px-1.5 py-0.5 rounded-sm">
                    {part.code}
                  </span>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-3 z-20">
                  <p className="font-display font-bold text-base uppercase tracking-wide text-white leading-tight">
                    {part.name}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="border-t border-zinc-200 dark:border-zinc-800 bg-white/95 dark:bg-zinc-950/95 backdrop-blur-md p-4 shrink-0">
        <button
          disabled={!selected || !serial.trim()}
          onClick={proceed}
          data-testid="new-inspection-continue-btn"
          className="group w-full bg-orange-500 hover:bg-orange-600 disabled:bg-zinc-200 dark:disabled:bg-zinc-800 disabled:text-zinc-400 dark:disabled:text-zinc-600 text-white font-display font-bold uppercase tracking-widest h-13 py-4 rounded-sm transition-all enabled:glow-orange active:scale-[0.98] flex items-center justify-center gap-2 disabled:cursor-not-allowed"
        >
          Continuar al checklist
          <ArrowRight className="w-4 h-4 group-enabled:group-hover:translate-x-1 transition-transform" />
        </button>
      </div>
    </div>
  );
}
